import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Check, ExternalLink, Gift, LoaderCircle, MapPin, Search, Sparkles } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, money } from "../../lib/api";
import type { ComplimentaryPlan, Trip } from "../../lib/types";

type Style = "budget" | "balanced" | "luxury";
type AddSelection = { plan: ComplimentaryPlan; durationProfile: string; style: Style };

const styles: Array<{ key: Style; label: string }> = [
  { key: "budget", label: "Value" },
  { key: "balanced", label: "Balanced" },
  { key: "luxury", label: "Luxury" },
];

function SuggestedPlanCard({
  plan,
  adding,
  onAdd,
}: {
  plan: ComplimentaryPlan;
  adding: boolean;
  onAdd: (selection: AddSelection) => void;
}) {
  const preferredDuration = plan.durations.find((item) => item.key === "medium") || plan.durations[Math.min(1, plan.durations.length - 1)] || plan.durations[0];
  const [durationProfile, setDurationProfile] = useState(preferredDuration?.key || "");
  const [style, setStyle] = useState<Style>("balanced");
  const duration = plan.durations.find((item) => item.key === durationProfile) || preferredDuration;
  if (!duration) return null;
  const cost = duration.costs[style];
  const claim = plan.claims.find((item) => item.duration_profile === duration.key && item.style === style);
  const location = [plan.region, plan.country].filter(Boolean).join(" · ");
  return <article className={`suggested-plan-card suggested-${plan.scope}`}>
    <div className={`suggested-plan-art${plan.hero_image ? " has-image" : ""}`}>
      {plan.hero_image && <img src={plan.hero_image} alt={`View of ${plan.destination}`} loading="lazy" />}
      <span>#{plan.rank} · {plan.scope === "india" ? "INDIA" : "INTERNATIONAL"}</span>
      <Gift size={28} />
      <div><small>{location}</small><h3>{plan.destination}</h3></div>
      {plan.hero_image_attribution && plan.hero_image_source_url && <a className="suggested-plan-credit" href={plan.hero_image_source_url} target="_blank" rel="noreferrer" aria-label={`Photo source for ${plan.destination}`}><ExternalLink size={11} />Photo source</a>}
    </div>
    <div className="suggested-plan-body">
      <div className="suggested-plan-tags">{plan.ideal_for.slice(0, 3).map((tag) => <span key={tag}>{tag.replaceAll("_", " ")}</span>)}</div>
      <p><MapPin size={14} />Base yourself around {plan.primary_stay_area}</p>
      <div className="suggested-plan-options">
        <label>Trip length<select value={duration.key} onChange={(event) => setDurationProfile(event.target.value)}>{plan.durations.map((item) => <option value={item.key} key={item.key}>{item.days} days · {item.key}</option>)}</select></label>
        <div><span>Travel style</span><div className="suggested-style-pills">{styles.map((item) => <button type="button" key={item.key} className={style === item.key ? "active" : ""} aria-pressed={style === item.key} onClick={() => setStyle(item.key)}>{item.label}</button>)}</div></div>
      </div>
      <div className="suggested-plan-cost"><span>Estimated ground plan</span><strong>{money(cost.minimum, cost.currency)}–{money(cost.maximum, cost.currency)}</strong><small>{cost.scope.replaceAll("_", " ")}</small></div>
      <div className="suggested-plan-season"><Sparkles size={14} /><span>Best around {plan.best_months.slice(0, 4).join(", ")}</span><a href={plan.google_maps_url} target="_blank" rel="noreferrer" aria-label={`Open ${plan.destination} in Google Maps`}><ExternalLink size={14} /></a></div>
      {claim?.trip_id ? <Link className="button primary suggested-plan-add" to={`/trips/${claim.trip_id}/overview`}><Check size={16} />Open in my trips<ArrowRight size={16} /></Link> : <button className="button primary suggested-plan-add" disabled={adding} onClick={() => onAdd({ plan, durationProfile: duration.key, style })}>{adding ? <><LoaderCircle className="location-spinner" size={16} />Adding your copy…</> : <><Gift size={16} />{claim ? "Add this plan again" : "Add to my trips"}<ArrowRight size={16} /></>}</button>}
    </div>
  </article>;
}

export function SuggestedPlansContent({
  plans,
  addingId,
  onAdd,
}: {
  plans: ComplimentaryPlan[];
  addingId?: string;
  onAdd: (selection: AddSelection) => void;
}) {
  const [scope, setScope] = useState<"all" | "india" | "international">("all");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(false);
  const filtered = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return plans.filter((plan) => (scope === "all" || plan.scope === scope) && (!query || [plan.destination, plan.region, plan.country, ...plan.ideal_for].some((value) => value?.toLocaleLowerCase().includes(query))));
  }, [plans, scope, search]);
  const visible = expanded ? filtered : filtered.slice(0, 12);

  return <>
    <div className="suggested-plan-toolbar">
      <div className="suggested-plan-filters" aria-label="Filter suggested plans">{(["all", "india", "international"] as const).map((value) => <button key={value} className={scope === value ? "active" : ""} onClick={() => { setScope(value); setExpanded(false); }}>{value === "all" ? "All plans" : value === "india" ? "India" : "International"}<span>{value === "all" ? plans.length : plans.filter((plan) => plan.scope === value).length}</span></button>)}</div>
      <label className="suggested-plan-search"><Search size={16} /><input aria-label="Search suggested plans" value={search} onChange={(event) => { setSearch(event.target.value); setExpanded(false); }} placeholder="Destination, country or mood" /></label>
    </div>
    {visible.length ? <div className="suggested-plan-grid">{visible.map((plan) => <SuggestedPlanCard key={plan.id} plan={plan} adding={addingId === plan.id} onAdd={onAdd} />)}</div> : <div className="suggested-plan-empty"><Search size={23} /><strong>No suggested plans match that search.</strong><span>Try another destination, country, or travel style.</span></div>}
    {!expanded && filtered.length > visible.length && <button className="button light suggested-plan-more" onClick={() => setExpanded(true)}>View all {filtered.length} suggested plans<ArrowRight size={16} /></button>}
  </>;
}

export function SuggestedPlans() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const plans = useQuery({
    queryKey: ["complimentary-plans"],
    queryFn: () => api<ComplimentaryPlan[]>("/complimentary-plans"),
    staleTime: 5 * 60_000,
  });
  const addPlan = useMutation({
    mutationFn: ({ plan, durationProfile, style }: AddSelection) => api<{ trip: Trip; created: boolean }>(`/complimentary-plans/${plan.id}/add`, {
      method: "POST",
      body: JSON.stringify({ duration_profile: durationProfile, style }),
    }),
    onSuccess: async ({ trip }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["trips"] }),
        queryClient.invalidateQueries({ queryKey: ["complimentary-plans"] }),
      ]);
      navigate(`/trips/${trip.id}/overview`);
    },
  });

  return <section className="suggested-plans page-width" aria-label="Suggested complimentary vacation plans">
    <header><div><p className="eyebrow">COMPLIMENTARY FROM RIHVO.FUN</p><h2>Suggested plans, ready to make yours.</h2><p>Choose a duration and travel style. We’ll copy the full itinerary into My Trips, where every day, activity, meal, budget, and date remains yours to change.</p></div><span><Gift size={18} />{plans.data?.length || 0} plans</span></header>
    {plans.isLoading ? <div className="suggested-plan-loading"><span className="skeleton" /><span className="skeleton" /><span className="skeleton" /></div> : plans.isError ? <div className="error-state"><p>We couldn’t load the complimentary plan catalogue.</p><button onClick={() => void plans.refetch()}>Try again</button></div> : plans.data?.length ? <SuggestedPlansContent plans={plans.data} addingId={addPlan.variables?.plan.id} onAdd={(selection) => addPlan.mutate(selection)} /> : <div className="suggested-plan-empty"><Gift size={25} /><strong>Complimentary plans are being prepared.</strong><span>They will appear here automatically when the Rihvo catalogue is published.</span></div>}
    {addPlan.isError && <p className="form-error suggested-plan-error">{addPlan.error.message}</p>}
  </section>;
}
